type EnumValueMapping = {
	name?: string;
	storageValue?: unknown;
	numericValue?: number;
	aliases?: string[];
};

type EnumMapping = {
	values?: EnumValueMapping[];
};

type QueryPlanLike = {
	catalogTables?: Array<{
		columns?: Array<{
			name: string;
			enumMapping?: EnumMapping;
		}>;
	}>;
};

const htmlEntityMap: Record<string, string> = {
	nbsp: " ",
	amp: "&",
	quot: "\"",
	apos: "'",
	lt: "<",
	gt: ">"
};

const hiddenColumnPatterns = [
	/^meilisearch/i,
	/^metadata$/i,
	/^elementssequence$/i,
	/^instructors$/i,
	/^slug$/i,
	/^labelid$/i,
	/^__source_id$/i,
	/^__raw_document$/i,
	/html$/i
];

export const stripHtml = (value: string): string => {
	if (!/[<&]/.test(value)) {
		return collapseWhitespace(value);
	}
	const withoutTags = value
		.replace(/<\s*br\s*\/?\s*>/gi, "\n")
		.replace(/<\s*\/\s*(p|div|section|article|li|h[1-6]|tr)\s*>/gi, "\n\n")
		.replace(/<\s*(p|div|section|article|li|h[1-6]|tr)\b[^>]*>/gi, "\n")
		.replace(/<[^>]*>/g, "");
	return collapseWhitespace(decodeHtmlEntities(withoutTags));
};

export const truncateText = (value: string, maxLength = 220): string =>
	value.length <= maxLength ? value : `${value.slice(0, maxLength - 3).trimEnd()}...`;

export const visibleColumnsForRows = (rows: Array<Record<string, unknown>>): string[] =>
	Object.keys(rows[0] ?? { sample: "" }).filter((column) => !isHiddenColumn(column));

export const formatCellValue = (
	row: Record<string, unknown>,
	column: string,
	queryPlan?: QueryPlanLike
): string => {
	const value = row[column];
	if (value === undefined || value === null) {
		return "";
	}
	const enumLabel = humanizeEnum(value, findColumnEnumMapping(column, queryPlan));
	if (enumLabel !== undefined) {
		return enumLabel;
	}
	if (typeof value === "string") {
		return stripHtml(value);
	}
	if (typeof value === "object") {
		return JSON.stringify(value);
	}
	return String(value);
};

export const humanizeEnum = (value: unknown, enumMapping?: EnumMapping): string | undefined => {
	const mappings = enumMapping?.values ?? [];
	const match = mappings.find((candidate) => {
		const storage = candidate.storageValue ?? candidate.numericValue;
		return String(storage) === String(value) || candidate.name === value;
	});
	if (match?.name) {
		return humanizeIdentifier(match.name);
	}
	return undefined;
};

export const humanizeKnownColumn = (column: string, value: unknown): string | undefined => {
	const normalizedColumn = column.toLowerCase();
	const normalizedValue = String(value);
	const known: Record<string, Record<string, string>> = {
		difficultylevel: {
			1: "Beginner",
			2: "Intermediate",
			3: "Advanced"
		},
		status: {
			0: "Active",
			1: "Draft",
			2: "Review",
			3: "Published",
			4: "Archived"
		},
		type: {
			0: "Module",
			1: "Course",
			2: "Certificate Program"
		}
	};
	if (typeof value === "string" && Number.isNaN(Number(value))) {
		return stripHtml(value);
	}
	return known[normalizedColumn]?.[normalizedValue];
};

export const formatBadgeValue = (
	row: Record<string, unknown>,
	column: string,
	queryPlan?: QueryPlanLike
): string | undefined => {
	const known = humanizeKnownColumn(column, row[column]);
	return (known ?? formatCellValue(row, column, queryPlan)) || undefined;
};

export const resolveRowStringField = (
	row: Record<string, unknown>,
	candidates: string[]
): string | undefined => {
	for (const key of candidates) {
		const actualKey = Object.keys(row).find(
			(candidate) => candidate.toLowerCase() === key.toLowerCase()
		);
		const value = actualKey ? row[actualKey] : undefined;
		if (typeof value === "string" && value.trim()) {
			return stripHtml(value.trim());
		}
	}
	return undefined;
};

export const resolveRowTitle = (row: Record<string, unknown>): string =>
	resolveRowStringField(row, ["title", "name", "label"]) ??
	resolveRowStringField(row, ["id"]) ??
	"Result";

export const resolveRowDescription = (row: Record<string, unknown>): string | undefined =>
	resolveRowStringField(row, ["description", "summary", "snippet", "shortdescription"]);

export const resolveRowDurationMinutes = (row: Record<string, unknown>): number | undefined => {
	for (const key of ["durationMinutes", "duration"]) {
		const actualKey = Object.keys(row).find(
			(candidate) => candidate.toLowerCase() === key.toLowerCase()
		);
		const value = actualKey ? row[actualKey] : undefined;
		if (typeof value === "number" && Number.isFinite(value)) {
			return Math.round(value);
		}
	}
	return undefined;
};

const findColumnEnumMapping = (
	column: string,
	queryPlan?: QueryPlanLike
): EnumMapping | undefined => queryPlan?.catalogTables
	?.flatMap((table) => table.columns ?? [])
	.find((candidate) => candidate.name.toLowerCase() === column.toLowerCase())
	?.enumMapping;

const isHiddenColumn = (column: string): boolean =>
	hiddenColumnPatterns.some((pattern) => pattern.test(column));

const decodeHtmlEntities = (value: string): string =>
	value.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, entity: string) => {
		const normalized = entity.toLowerCase();
		if (normalized.startsWith("#x")) {
			const codePoint = Number.parseInt(normalized.slice(2), 16);
			return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : match;
		}
		if (normalized.startsWith("#")) {
			const codePoint = Number.parseInt(normalized.slice(1), 10);
			return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : match;
		}
		return htmlEntityMap[normalized] ?? match;
	});

const collapseWhitespace = (value: string): string =>
	value
		.replace(/\r\n/g, "\n")
		.replace(/[ \t\f\v]+/g, " ")
		.replace(/ *\n */g, "\n")
		.replace(/\n{3,}/g, "\n\n")
		.trim();

const bareUrlPattern = /(?<!\]\()(?<!<)(https?:\/\/[^\s<>")\]]+)/gi;

/** Turn plain URLs into markdown links so ReactMarkdown renders clickable anchors. */
export const linkifyBareUrls = (value: string): string =>
	value.replace(bareUrlPattern, (url) => {
		const trailingPunctuation = url.match(/([.,;:!?]+)$/);
		if (!trailingPunctuation || url.endsWith("/")) {
			return `[${url}](${url})`;
		}
		const cleanUrl = url.slice(0, -trailingPunctuation[1].length);
		return `[${cleanUrl}](${cleanUrl})${trailingPunctuation[1]}`;
	});

/** Prepare streamed LLM markdown for ReactMarkdown (avoids double spacing with pre-wrap). */
export const normalizeAssistantMarkdown = (value: string): string => {
	let normalized = stripHtml(value).replace(/\r\n/g, "\n");
	normalized = normalized.replace(/\n{3,}/g, "\n\n");
	// "1.\n\nTitle" -> "1. Title" so ordered lists parse as a single item line.
	normalized = normalized.replace(/^(\s*\d+\.)\s*\n+(?=\S)/gm, "$1 ");
	normalized = linkifyBareUrls(normalized);
	return normalized.trim();
};

const whyItFitsPattern = /\s*\*{0,4}Why it fits:\*{0,4}\s*/gi;

const formatBoldMarkdownLinkTitle = (label: string, url: string): string => {
	const trimmed = label.trim().replace(/^\*\*(.+)\*\*$/, "$1").trim();
	return `[**${trimmed}**](${url})`;
};

const boldifyMarkdownLinkTitles = (value: string): string =>
	value
		.replace(/\*\*\[([^\]]+)\]\(([^)]+)\)\*\*/g, (_match, label: string, url: string) =>
			formatBoldMarkdownLinkTitle(label, url)
		)
		.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, label: string, url: string) => {
			const trimmed = label.trim();
			if (/^\*\*.+\*\*$/.test(trimmed)) {
				return match;
			}
			return formatBoldMarkdownLinkTitle(trimmed, url);
		});

/** Improve study-plan markdown from Meilisearch Chat for chat rendering. */
export const normalizeRecommendMarkdown = (value: string): string => {
	let normalized = normalizeAssistantMarkdown(value);

	normalized = normalized.replace(/^[ \t]*-{3,}[ \t]*$/gm, "\n\n---\n\n");
	normalized = normalized.replace(/\n---\n/g, "\n\n---\n\n");
	normalized = boldifyMarkdownLinkTitles(normalized);
	normalized = normalized.replace(whyItFitsPattern, "\n\n**Why it fits:**\n\n");
	normalized = normalized.replace(/(\n)(#{1,3}\s*Phase\s+\d+)/gi, "$1\n$2");
	normalized = normalized.replace(/(\n)(\*\*Phase\s+\d+[^*]*\*\*)/gi, "$1\n$2");
	normalized = normalized.replace(/(\.)(\n)(\*?\*?Phase\s+\d+)/gi, "$1\n\n$3");
	normalized = normalized.replace(/\n{4,}/g, "\n\n\n");

	return normalized.trim();
};

const humanizeIdentifier = (value: string): string =>
	value
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/[_-]+/g, " ")
		.trim();
